import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import { qs } from "../dateRange";
import type { Capability, Paginated } from "../../types/api";

// ----------------------------------------------------------------- config types
export type FieldType =
  | "text"
  | "number"
  | "select"
  | "boolean"
  | "textarea"
  | "json"
  | "password"
  | "reference";

/** A searchable link to another entity — the user picks a name, we store the id. */
export interface ReferenceDef {
  endpoint: string; // list endpoint to search, e.g. "sites"
  optionLabel: string; // field on the option row to display, e.g. "title"
  optionSublabel?: string; // secondary line on each option, e.g. "email"
  labelFrom?: string; // field on the current record holding the denormalized label
}

export interface FieldDef {
  name: string;
  label: string;
  type: FieldType;
  options?: { value: string | number; label: string }[];
  reference?: ReferenceDef;
  required?: boolean;
  help?: string;
  placeholder?: string;
  readOnly?: boolean;
  step?: number;
}

export interface ColumnDef {
  key: string;
  label: string;
  sortable?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render?: (row: any) => ReactNode;
  width?: string;
}

export interface RelationDef {
  label: string;
  endpoint: string;
  filterParam: string; // query param to filter related rows by this record's id
  columns: ColumnDef[];
}

export interface ResourceConfig {
  key: string;
  label: string;
  singular: string;
  endpoint: string;
  group: string;
  section: "platform" | "tenant"; // platform administration vs tenant administration
  icon: string;
  titleField: string;
  auditLabel?: string; // matches backend AuditLog object_type
  columns: ColumnDef[];
  fields: FieldDef[];
  relations?: RelationDef[];
  readOnly?: boolean;
  noEdit?: boolean; // create + delete allowed, but no in-place edit (e.g. API keys)
  // Capability required to create/edit/delete. Read is allowed for any member.
  // Mirrors the backend catalogue so UI and API gate identically.
  capability?: Capability;
  searchable?: boolean;
  supportsBulk?: boolean;
}

/** Can the current principal (given its active-tenant capabilities) write this resource? */
export function canWrite(cfg: ResourceConfig, capabilities: Capability[]): boolean {
  if (cfg.readOnly) return false;
  if (!cfg.capability) return capabilities.length > 0;
  return capabilities.includes(cfg.capability);
}

// ----------------------------------------------------------------- generic hooks
export interface ListParams {
  limit?: number;
  offset?: number;
  ordering?: string;
  search?: string;
  [k: string]: string | number | boolean | undefined;
}

export function useResourceList(endpoint: string, params: ListParams) {
  const { tenant } = useAuth();
  const query = qs({ limit: 25, ...params });
  return useQuery({
    queryKey: ["resource", endpoint, tenant?.slug ?? "all", query],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    queryFn: async () => (await api.get<Paginated<any>>(`/${endpoint}/${query}`)).data,
    placeholderData: (prev) => prev,
  });
}

export function useResourceCreate(endpoint: string) {
  const qc = useQueryClient();
  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: async (body: Record<string, any>) => (await api.post(`/${endpoint}/`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resource", endpoint] }),
  });
}

export function useResourceUpdate(endpoint: string) {
  const qc = useQueryClient();
  return useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: async ({ id, body }: { id: number; body: Record<string, any> }) =>
      (await api.patch(`/${endpoint}/${id}/`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resource", endpoint] }),
  });
}

export function useResourceDelete(endpoint: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => (await api.delete(`/${endpoint}/${id}/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resource", endpoint] }),
  });
}

export function useResourceBulk(endpoint: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ action, ids }: { action: string; ids: number[] }) =>
      (await api.post(`/${endpoint}/bulk/`, { action, ids })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["resource", endpoint] }),
  });
}

export function useRelated(endpoint: string, filterParam: string, id: number | null) {
  return useQuery({
    queryKey: ["related", endpoint, filterParam, id],
    enabled: id != null,
    queryFn: async () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (await api.get<Paginated<any>>(`/${endpoint}/${qs({ [filterParam]: id!, limit: 50 })}`)).data,
  });
}
